import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE, TENANT } from '@/api/client';
import { keys } from '@/api/hooks';

/**
 * Tiempo real (SSE, spec §2): los proyectos que la pantalla muestra. Cualquier cambio de otra persona invalida las
 * consultas afectadas; las vistas se repintan solas. Los avisos personales llegan por el mismo canal.
 */
export function useRealtime(projectIds: string[]) {
  const qc = useQueryClient();
  const ids = [...new Set(projectIds)].sort().join(',');
  useEffect(() => {
    const es = new EventSource(`${API_BASE}/realtime/stream?projectIds=${encodeURIComponent(ids)}&tenant=${encodeURIComponent(TENANT)}`, { withCredentials: true });
    let pending: ReturnType<typeof setTimeout> | null = null;
    const touched = new Set<string>();
    const flush = () => {
      pending = null;
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      for (const id of touched) void qc.invalidateQueries({ queryKey: keys.task(id) });
      touched.clear();
    };
    const onTask = (e: MessageEvent) => {
      const data = JSON.parse(e.data) as { data: { taskId?: string } };
      if (data.data.taskId) touched.add(data.data.taskId);
      pending ??= setTimeout(flush, 250);
    };
    for (const t of ['task.created', 'task.updated', 'task.moved', 'task.removed', 'comment.created', 'comment.changed']) es.addEventListener(t, onTask);
    es.addEventListener('section.changed', (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { projectId: string };
      void qc.invalidateQueries({ queryKey: keys.project(d.projectId) });
    });
    es.addEventListener('view.changed', (e) => {
      const d = JSON.parse((e as MessageEvent).data) as { projectId: string };
      void qc.invalidateQueries({ queryKey: keys.project(d.projectId) });
    });
    es.addEventListener('project.updated', () => void qc.invalidateQueries({ queryKey: ['project'] }));
    es.addEventListener('notification', () => {
      void qc.invalidateQueries({ queryKey: ['notifications'] });
    });
    es.addEventListener('decision.completed', () => {
      void qc.invalidateQueries({ queryKey: ['decisions'] });
      void qc.invalidateQueries({ queryKey: ['intake'] });
    });
    return () => {
      if (pending) clearTimeout(pending);
      es.close();
    };
  }, [ids, qc]);
}
